import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import Button from "@/components/Button";
import Skeleton from "@/components/Skeleton";
import { colorVar } from "@/components/CourseContextBar";
import { useToast } from "@/components/Toast";
import {
  listMyCourses, createCourse,
  listAllCourses, previewCourse, copyCourseToMine,
  type CoursePreview,
} from "@/api/course";
import type { Course, LibraryCourse } from "@/types";

const GRID = "minmax(160px,1.4fr) 110px minmax(130px,1.6fr) 70px 96px 96px";

type Tab = "mine" | "library";

/** Liste des cours d'un enseignant + bibliothèque inter-profs.
 *  Le prop `archived` bascule entre les cours actifs et les archives.
 *  La bibliothèque (tab "Bibliothèque") n'est visible que sur la vue non-archivée. */
export default function CoursesList({ archived = false }: { archived?: boolean }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("mine");

  // ── Mes cours ──────────────────────────────────────────────────────────────
  const [courses, setCourses] = useState<Course[] | null>(null);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { listMyCourses().then(setCourses).catch(() => setCourses([])); }, []);

  const allClasses = useMemo(() => {
    const s = new Set<string>();
    (courses ?? []).forEach((c) => c.class_names.forEach((n) => s.add(n)));
    return [...s].sort();
  }, [courses]);

  const q = search.trim().toLowerCase();
  const filtered = (courses ?? [])
    .filter((c) => c.archived === archived)
    .filter((c) => !q || c.title.toLowerCase().includes(q) || c.join_code.toLowerCase().includes(q))
    .filter((c) => !classFilter || c.class_names.includes(classFilter));

  const create = async () => {
    setCreating(true); setError(null);
    try {
      const c = await createCourse("Nouveau cours");
      navigate(`/instructor/courses/${c.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Échec de la création.");
      setCreating(false);
    }
  };

  // ── Bibliothèque inter-profs ───────────────────────────────────────────────
  const [library, setLibrary] = useState<LibraryCourse[] | null>(null);
  const [libSearch, setLibSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [previews, setPreviews] = useState<Map<number, CoursePreview | "loading" | "error">>(new Map());
  const [copying, setCopying] = useState<Set<number>>(new Set());
  // avoid duplicate fetches
  const fetchedIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (tab === "library" && library === null) {
      listAllCourses().then(setLibrary).catch(() => setLibrary([]));
    }
  }, [tab, library]);

  const libQ = libSearch.trim().toLowerCase();
  const libFiltered = (library ?? [])
    .filter((c) => !c.mine)
    .filter((c) => !libQ || c.title.toLowerCase().includes(libQ)
      || (c.teacher ?? "").toLowerCase().includes(libQ));

  // Group by teacher for readability
  const libGroups = useMemo(() => {
    const map = new Map<string, LibraryCourse[]>();
    for (const c of libFiltered) {
      const key = c.teacher ?? "—";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [libFiltered]);

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); return next; }
      next.add(id);
      if (!fetchedIds.current.has(id)) {
        fetchedIds.current.add(id);
        setPreviews((p) => new Map(p).set(id, "loading"));
        previewCourse(id)
          .then((data) => setPreviews((p) => new Map(p).set(id, data)))
          .catch(() => setPreviews((p) => new Map(p).set(id, "error")));
      }
      return next;
    });
  };

  const copyToMine = async (course: LibraryCourse) => {
    setCopying((prev) => new Set(prev).add(course.id));
    try {
      const created = await copyCourseToMine(course.id);
      toast.show(`« ${created.title} » copié dans votre espace.`, "success");
      navigate(`/instructor/courses/${created.id}`);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : "Échec de la copie.", "error");
      setCopying((prev) => { const s = new Set(prev); s.delete(course.id); return s; });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <style>{`
        .pl-course-row:hover { background: var(--bg-bar); }
        .pl-course-row:focus-visible { outline: 2px solid var(--blue); outline-offset: -2px; }
        .pl-lib-card:hover .pl-lib-card-inner { background: var(--bg-bar); }
      `}</style>

      <div style={styles.head}>
        <div>
          <p style={styles.crumb}>Espace enseignant</p>
          <h1 style={styles.title}>{archived ? "Archives" : "Cours"}</h1>
        </div>
        {!archived && tab === "mine" && (
          <Button variant="primary" onClick={create} disabled={creating}>
            {creating ? <span className="spin" /> : <><Icon name="checklist" size={15} /> Créer un cours</>}
          </Button>
        )}
      </div>

      {!archived && (
        <div style={styles.tabs}>
          <button style={{ ...styles.tab, ...(tab === "mine" ? styles.tabActive : {}) }} onClick={() => setTab("mine")} type="button">
            <Icon name="checklist" size={14} /> Mes cours
          </button>
          <button style={{ ...styles.tab, ...(tab === "library" ? styles.tabActive : {}) }} onClick={() => setTab("library")} type="button">
            <Icon name="search" size={14} /> Bibliothèque
          </button>
        </div>
      )}

      {/* ── Mes cours ─────────────────────────────────────────────────────── */}
      {(archived || tab === "mine") && (
        <>
          {error && <div style={styles.error}><Icon name="alert" size={15} style={{ color: "var(--red)", flexShrink: 0 }} /> {error}</div>}
          <div style={styles.toolbar}>
            <div style={styles.searchBox}>
              <Icon name="search" size={15} style={{ color: "var(--text-faint)", flexShrink: 0 }} />
              <input style={styles.searchInput} placeholder="Rechercher un cours, un code…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select style={styles.select} value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
              <option value="">Toutes les classes</option>
              {allClasses.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div style={styles.card}>
            <div style={styles.headRow}>
              <span>Cours</span>
              <span>Code</span>
              <span>Classes</span>
              <span style={styles.center}>Élèves</span>
              <span>Statut</span>
              <span />
            </div>

            {courses === null ? (
              <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={40} />)}
              </div>
            ) : filtered.length === 0 ? (
              <div style={styles.emptyState}>
                <Icon name="checklist" size={40} style={{ color: "var(--text-faint)" }} />
                {archived ? (
                  <p style={styles.emptyHint}>Aucun cours archivé.</p>
                ) : (q || classFilter) ? (
                  <p style={styles.emptyHint}>Aucun cours ne correspond à la recherche.</p>
                ) : (
                  <>
                    <p style={styles.emptyTitle}>Vous n'avez pas encore de cours</p>
                    <p style={styles.emptyHint}>Créez votre premier cours ou copiez un cours depuis la <button type="button" style={styles.inlineLink} onClick={() => setTab("library")}>Bibliothèque</button>.</p>
                    <Button variant="primary" onClick={create} disabled={creating}>
                      {creating ? <span className="spin" /> : <><Icon name="checklist" size={15} /> Créer un cours</>}
                    </Button>
                  </>
                )}
              </div>
            ) : (
              filtered.map((c) => (
                <button key={c.id} className="pl-course-row" style={styles.row} onClick={() => navigate(`/instructor/courses/${c.id}`)} type="button">
                  <span style={styles.titleCell}>
                    <span style={{ ...styles.dot, background: colorVar(c.color) }} />
                    <span style={styles.courseName}>{c.title}</span>
                  </span>
                  <span style={styles.code}>{c.join_code}</span>
                  <span style={styles.classCell}>
                    {c.class_names.length === 0
                      ? <span style={styles.none}>—</span>
                      : c.class_names.map((n) => <span key={n} style={styles.tag}>{n}</span>)}
                  </span>
                  <span style={styles.numCell}>{c.student_count}</span>
                  <span>
                    <span style={{ ...styles.status, ...(c.archived ? styles.statusArchived : styles.statusActive) }}>
                      {c.archived ? "Archivé" : "Actif"}
                    </span>
                  </span>
                  <span style={styles.manage}>Gérer <Icon name="next" size={14} /></span>
                </button>
              ))
            )}
          </div>
        </>
      )}

      {/* ── Bibliothèque inter-profs ───────────────────────────────────────── */}
      {!archived && tab === "library" && (
        <div>
          <p style={styles.libHint}>
            Parcourez les cours des autres enseignants. Copiez un cours pour l'adapter dans votre espace — les séries sont importées en brouillon.
          </p>

          <div style={{ ...styles.toolbar, marginBottom: 20 }}>
            <div style={styles.searchBox}>
              <Icon name="search" size={15} style={{ color: "var(--text-faint)", flexShrink: 0 }} />
              <input style={styles.searchInput} placeholder="Rechercher un cours ou un enseignant…" value={libSearch} onChange={(e) => setLibSearch(e.target.value)} />
            </div>
          </div>

          {library === null ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={72} />)}
            </div>
          ) : libFiltered.length === 0 ? (
            <div style={styles.emptyState}>
              <Icon name="search" size={40} style={{ color: "var(--text-faint)" }} />
              <p style={styles.emptyHint}>
                {libSearch ? "Aucun cours ne correspond à la recherche." : "Aucun cours partagé pour l'instant."}
              </p>
            </div>
          ) : (
            libGroups.map(([teacher, libCourses]) => (
              <div key={teacher} style={styles.libGroup}>
                <p style={styles.libTeacher}>{teacher}</p>
                <div style={styles.libList}>
                  {libCourses.map((c) => {
                    const isExpanded = expanded.has(c.id);
                    const preview = previews.get(c.id);
                    const isCopying = copying.has(c.id);
                    return (
                      <div key={c.id} className="pl-lib-card" style={styles.libCard}>
                        <div className="pl-lib-card-inner" style={styles.libCardInner}>
                          <span style={{ ...styles.libColorStrip, background: colorVar(c.color) }} />
                          <span style={styles.libTitle}>{c.title}</span>
                          <span style={styles.libActions}>
                            <button
                              type="button"
                              style={{ ...styles.libBtn, ...(isExpanded ? styles.libBtnActive : {}) }}
                              onClick={() => toggleExpand(c.id)}
                              aria-expanded={isExpanded}
                            >
                              <Icon name={isExpanded ? "up" : "down"} size={13} />
                              Aperçu
                            </button>
                            <button
                              type="button"
                              style={{ ...styles.libBtn, ...styles.libBtnCopy }}
                              onClick={() => copyToMine(c)}
                              disabled={isCopying}
                            >
                              {isCopying
                                ? <span className="spin" style={{ width: 12, height: 12 }} />
                                : <Icon name="duplicate" size={13} />}
                              Copier dans mon espace
                            </button>
                          </span>
                        </div>

                        {isExpanded && (
                          <div style={styles.libPreview}>
                            {preview === "loading" && (
                              <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 20px 16px" }}>
                                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={24} />)}
                              </div>
                            )}
                            {preview === "error" && (
                              <p style={styles.libPreviewEmpty}>Impossible de charger l'aperçu.</p>
                            )}
                            {preview && typeof preview === "object" && (
                              preview.exercises.length === 0 ? (
                                <p style={styles.libPreviewEmpty}>Aucune série dans ce cours.</p>
                              ) : (
                                <ul style={styles.libExList}>
                                  {preview.exercises.map((ex) => (
                                    <li key={ex.id} style={styles.libExItem}>
                                      <span style={{ ...styles.libExStatus, ...(ex.published ? styles.libExPublished : styles.libExDraft) }} />
                                      <span style={styles.libExTitle}>{ex.title}</span>
                                      <span style={styles.libExMeta}>{ex.step_count} exercice{ex.step_count !== 1 ? "s" : ""}</span>
                                    </li>
                                  ))}
                                </ul>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  head: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginBottom: 20 },
  crumb: { margin: 0, fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1 },
  title: { margin: "4px 0 0", fontSize: 26, fontWeight: 700, color: "var(--text)" },
  error: { display: "flex", alignItems: "center", gap: 8, marginBottom: 16, padding: "8px 12px", background: "var(--err-box-bg)", color: "var(--red)", borderRadius: "var(--radius)", fontSize: 13 },

  // Tabs
  tabs: { display: "flex", gap: 2, marginBottom: 20, background: "var(--bg-bar)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius)", padding: 3, width: "fit-content" },
  tab: { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: "calc(var(--radius) - 2px)", border: "none", background: "transparent", color: "var(--text-muted)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "background 0.12s, color 0.12s" },
  tabActive: { background: "var(--bg-panel)", color: "var(--blue)", boxShadow: "0 1px 3px rgba(0,0,0,.12)" },

  toolbar: { display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 220, background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "8px 12px" },
  searchInput: { flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none", color: "var(--text)", fontSize: 14, fontFamily: "inherit" },
  select: { background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: "var(--text)", fontSize: 14, padding: "0 12px", fontFamily: "inherit", cursor: "pointer" },

  card: { background: "var(--bg-panel)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius-lg)", overflow: "hidden" },
  headRow: {
    display: "grid", gridTemplateColumns: GRID, gap: 12, alignItems: "center",
    padding: "12px 18px", borderBottom: "1px solid var(--border-soft)", background: "var(--bg-bar)",
    fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-muted)",
  },
  center: { textAlign: "center" },
  row: {
    display: "grid", gridTemplateColumns: GRID, gap: 12, alignItems: "center", width: "100%",
    padding: "14px 18px", borderBottom: "1px solid var(--border-soft)", background: "transparent",
    border: "none", borderTop: "none", borderLeft: "none", borderRight: "none",
    cursor: "pointer", textAlign: "left", color: "var(--text)", fontFamily: "inherit",
  },
  titleCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  dot: { width: 11, height: 11, borderRadius: "50%", flexShrink: 0 },
  courseName: { fontWeight: 600, fontSize: 14.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  code: { fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 13, letterSpacing: 1, color: "var(--text)" },
  classCell: { display: "flex", flexWrap: "wrap", gap: 4, minWidth: 0 },
  tag: { fontSize: 11.5, fontWeight: 600, background: "var(--badge-bg)", color: "var(--blue)", borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" },
  none: { color: "var(--text-faint)" },
  numCell: { textAlign: "center", fontWeight: 600, color: "var(--text)", fontVariantNumeric: "tabular-nums" },
  status: { fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: "2px 10px", whiteSpace: "nowrap" },
  statusActive: { background: "var(--ok-box-bg)", color: "var(--green)" },
  statusArchived: { background: "var(--bg-bar)", color: "var(--text-muted)", border: "1px solid var(--border)" },
  manage: { display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 4, fontSize: 13, fontWeight: 700, color: "var(--blue)" },
  emptyState: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: "48px 24px", textAlign: "center" },
  emptyTitle: { margin: 0, fontSize: 17, fontWeight: 600, color: "var(--text)" },
  emptyHint: { margin: "0 0 8px", fontSize: 13.5, color: "var(--text-muted)", maxWidth: 420 },
  inlineLink: { background: "none", border: "none", padding: 0, color: "var(--blue)", fontWeight: 600, cursor: "pointer", fontSize: "inherit", fontFamily: "inherit", textDecoration: "underline" },

  // Bibliothèque
  libHint: { margin: "0 0 20px", fontSize: 13.5, color: "var(--text-muted)", maxWidth: 620 },
  libGroup: { marginBottom: 24 },
  libTeacher: { margin: "0 0 8px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "var(--text-muted)" },
  libList: { display: "flex", flexDirection: "column", gap: 2, background: "var(--bg-panel)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius-lg)", overflow: "hidden" },
  libCard: { borderBottom: "1px solid var(--border-soft)" },
  libCardInner: { display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", transition: "background 0.1s" },
  libColorStrip: { width: 4, height: 32, borderRadius: 2, flexShrink: 0 },
  libTitle: { flex: 1, fontWeight: 600, fontSize: 14.5, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  libActions: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  libBtn: {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "5px 11px", borderRadius: "var(--radius)", border: "1px solid var(--border)",
    background: "var(--bg-bar)", color: "var(--text-muted)", fontSize: 12.5, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "background 0.1s, color 0.1s",
  },
  libBtnActive: { background: "var(--badge-bg)", color: "var(--blue)", borderColor: "var(--blue)" },
  libBtnCopy: { background: "var(--badge-bg)", color: "var(--blue)", borderColor: "transparent" },

  libPreview: { borderTop: "1px solid var(--border-soft)", background: "var(--bg-base)" },
  libPreviewEmpty: { margin: 0, padding: "12px 20px 14px", fontSize: 13, color: "var(--text-faint)", fontStyle: "italic" },
  libExList: { margin: 0, padding: "10px 20px 14px", listStyle: "none", display: "flex", flexDirection: "column", gap: 6 },
  libExItem: { display: "flex", alignItems: "center", gap: 10 },
  libExStatus: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  libExPublished: { background: "var(--green)" },
  libExDraft: { background: "var(--border)" },
  libExTitle: { flex: 1, fontSize: 13.5, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  libExMeta: { fontSize: 11.5, color: "var(--text-faint)", flexShrink: 0, fontVariantNumeric: "tabular-nums" },
};
